import { useCallback, useEffect, useState } from "react";
import type { User } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  buildDictionaryWord,
  toFirestoreDictionaryWord,
} from "../lib/dictionaryWord";
import { normalizeWordId } from "../lib/text";
import type { DictionaryWord, WordInsight } from "../types";

const LOCAL_KEY = "vibetranslations-guest-dictionary";

function readGuestDictionary(): DictionaryWord[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) ?? "[]") as DictionaryWord[];
  } catch {
    return [];
  }
}

export function useDictionary(user: User | null) {
  const [words, setWords] = useState<DictionaryWord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setWords(readGuestDictionary());
      setLoading(false);
      return;
    }

    setLoading(true);
    const wordsRef = collection(db, "users", user.uid, "words");
    return onSnapshot(
      wordsRef,
      (snapshot) => {
        const nextWords = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }) as DictionaryWord)
          .sort((a, b) => a.word.localeCompare(b.word));
        setWords(nextWords);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );
  }, [user]);

  const addWord = useCallback(
    async (
      insight: WordInsight,
      context: string,
      targetLanguage: string,
      sourceLanguage = insight.detectedLanguage,
    ) => {
      const id = normalizeWordId(insight.word, targetLanguage);
      const dictionaryWord = buildDictionaryWord({
        id,
        insight,
        sourceLanguage,
        targetLanguage,
        context,
      });

      if (!user) {
        const nextWords = [dictionaryWord, ...words.filter((item) => item.id !== id)];
        localStorage.setItem(LOCAL_KEY, JSON.stringify(nextWords));
        setWords(nextWords);
        return;
      }

      await setDoc(
        doc(db, "users", user.uid, "words", id),
        {
          ...toFirestoreDictionaryWord(dictionaryWord),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    },
    [user, words],
  );

  const removeWord = useCallback(
    async (id: string) => {
      if (!user) {
        const nextWords = words.filter((item) => item.id !== id);
        localStorage.setItem(LOCAL_KEY, JSON.stringify(nextWords));
        setWords(nextWords);
        return;
      }

      await deleteDoc(doc(db, "users", user.uid, "words", id));
    },
    [user, words],
  );

  return { words, loading, addWord, removeWord };
}
